import axios from 'axios';
import { API_BASE_URL } from './config';

// Base axios instance; token/header injection handled elsewhere (e.g., App authAxios)
const api = axios.create({
  baseURL: API_BASE_URL,
});

// --- Generic helpers ---
export const fetchInvoice = async (authAxios, id) => {
  const resp = await authAxios.get(`invoices/${id}/`);
  return resp.data;
};

export const patchInvoiceItems = async (authAxios, id, items) => {
  const payload = { items };
  const resp = await authAxios.patch(`invoices/${id}/`, payload);
  return resp.data;
};

export const createInvoiceJournal = async (authAxios, invoiceId, journal_type, amount, notes='') => {
  const payload = { invoice: invoiceId, journal_type, amount, notes };
  const resp = await authAxios.post('invoice-journals/', payload);
  return resp.data;
};

export const listInvoiceJournals = async (authAxios, invoiceId) => {
  const resp = await authAxios.get('invoice-journals/', { params: { invoice: invoiceId } });
  return resp.data?.results || resp.data; // support paginated or flat
};

// --- Collections: settings and templates ---
export const getCollectionsSettings = async (authAxios) => {
  const resp = await authAxios.get('collections/settings/');
  return resp.data;
};

export const updateCollectionsSettings = async (authAxios, payload) => {
  const resp = await authAxios.put('collections/settings/', payload);
  return resp.data;
};

export const listEmailTemplates = async (authAxios, params = {}) => {
  const resp = await authAxios.get('email-templates/', { params });
  return resp.data?.results || resp.data;
};

export const createEmailTemplate = async (authAxios, data) => {
  const resp = await authAxios.post('email-templates/', data);
  return resp.data;
};

export const updateEmailTemplate = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`email-templates/${id}/`, data);
  return resp.data;
};

export const deleteEmailTemplate = async (authAxios, id) => {
  await authAxios.delete(`email-templates/${id}/`);
};

// --- Invoice-level collections actions ---
export const collectionsPreviewInvoice = async (authAxios, invoiceId) => {
  const resp = await authAxios.get(`invoices/${invoiceId}/collections/preview/`);
  return resp.data;
};

export const collectionsSendInvoice = async (authAxios, invoiceId) => {
  const resp = await authAxios.post(`invoices/${invoiceId}/collections/send/`);
  return resp.data;
};

export const collectionsAdvanceInvoice = async (authAxios, invoiceId, sendNow=false) => {
  const resp = await authAxios.post(`invoices/${invoiceId}/collections/advance/`, { send_now: !!sendNow });
  return resp.data;
};

// --- Dunning communication logs ---
export const listDunningLogsForInvoice = async (authAxios, invoiceId, limit=20) => {
  const resp = await authAxios.get('communication-logs/', { params: { invoice: invoiceId, collections_only: 'true' } });
  const data = resp.data?.results || resp.data;
  return Array.isArray(data) ? data.slice(0, limit) : [];
};

// --- WIP (Work In Progress) invoicing helpers ---
export const listTimeEntries = async (authAxios, params={}) => {
  const resp = await authAxios.get('time-entries/', { params });
  return resp.data?.results || resp.data;
};

export const listExpenseEntries = async (authAxios, params={}) => {
  const resp = await authAxios.get('expense-entries/', { params });
  return resp.data?.results || resp.data;
};

export const listClients = async (authAxios, params={}) => {
  const resp = await authAxios.get('clients/', { params });
  return resp.data?.results || resp.data;
};

export const listCases = async (authAxios, params={}) => {
  const resp = await authAxios.get('cases/', { params });
  return resp.data?.results || resp.data;
};

export const generateInvoiceFromWip = async (authAxios, payload) => {
  // payload: { client?, case?, time_entry_ids?, expense_entry_ids? }
  const resp = await authAxios.post('invoices/generate_from_wip/', payload);
  return resp.data;
};

// --- Task Template helpers ---
export const listTaskTemplates = async (authAxios, params = {}) => {
  const resp = await authAxios.get('task-templates/', { params });
  return resp.data?.results || resp.data;
};

export const createTaskTemplate = async (authAxios, data) => {
  const resp = await authAxios.post('task-templates/', data);
  return resp.data;
};

export const updateTaskTemplate = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`task-templates/${id}/`, data);
  return resp.data;
};

export const deactivateTaskTemplate = async (authAxios, id) => {
  const resp = await authAxios.post(`task-templates/${id}/deactivate/`);
  return resp.data;
};

export const activateTaskTemplate = async (authAxios, id) => {
  const resp = await authAxios.post(`task-templates/${id}/activate/`);
  return resp.data;
};

export const saveStageTaskTemplates = async (authAxios, caseId, overwrite=true) => {
  const resp = await authAxios.post(`cases/${caseId}/save_stage_task_templates/`, { overwrite });
  return resp.data;
};

// =============================================================================
// ACCOUNTING MODULE – Phase 1 API helpers
// =============================================================================

// --- Chart of Accounts (GL Accounts) ---
export const listGLAccounts = async (authAxios, params={}) => {
  const resp = await authAxios.get('gl-accounts/', { params });
  return resp.data?.results || resp.data;
};
export const createGLAccount = async (authAxios, data) => {
  const resp = await authAxios.post('gl-accounts/', data);
  return resp.data;
};
export const updateGLAccount = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`gl-accounts/${id}/`, data);
  return resp.data;
};
export const deleteGLAccount = async (authAxios, id) => {
  await authAxios.delete(`gl-accounts/${id}/`);
};
export const seedGLAccounts = async (authAxios) => {
  const resp = await authAxios.post('gl-accounts/seed/');
  return resp.data;
};

// --- Journal Entries ---
export const listJournalEntries = async (authAxios, params={}) => {
  const resp = await authAxios.get('gl-journal-entries/', { params });
  return resp.data?.results || resp.data;
};
export const createManualJournal = async (authAxios, data) => {
  const resp = await authAxios.post('gl-journal-entries/create-manual/', data);
  return resp.data;
};
export const reverseJournalEntry = async (authAxios, id) => {
  const resp = await authAxios.post(`gl-journal-entries/${id}/reverse/`);
  return resp.data;
};

// --- Suppliers ---
export const listSuppliers = async (authAxios, params={}) => {
  const resp = await authAxios.get('suppliers/', { params });
  return resp.data?.results || resp.data;
};
export const createSupplier = async (authAxios, data) => {
  const resp = await authAxios.post('suppliers/', data);
  return resp.data;
};
export const updateSupplier = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`suppliers/${id}/`, data);
  return resp.data;
};
export const deleteSupplier = async (authAxios, id) => {
  await authAxios.delete(`suppliers/${id}/`);
};

// --- AP Bills ---
export const listAPBills = async (authAxios, params={}) => {
  const resp = await authAxios.get('ap-bills/', { params });
  return resp.data?.results || resp.data;
};
export const createAPBill = async (authAxios, data) => {
  const resp = await authAxios.post('ap-bills/', data);
  return resp.data;
};
export const updateAPBill = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`ap-bills/${id}/`, data);
  return resp.data;
};
export const approveAPBill = async (authAxios, id) => {
  const resp = await authAxios.post(`ap-bills/${id}/approve/`);
  return resp.data;
};
export const recordAPBillPayment = async (authAxios, id, data) => {
  const resp = await authAxios.post(`ap-bills/${id}/record-payment/`, data);
  return resp.data;
};

// --- AP Payments (read-only) ---
export const listAPPayments = async (authAxios, params={}) => {
  const resp = await authAxios.get('ap-payments/', { params });
  return resp.data?.results || resp.data;
};

// --- Petty Cash ---
export const listPettyCashFunds = async (authAxios, params={}) => {
  const resp = await authAxios.get('petty-cash-funds/', { params });
  return resp.data?.results || resp.data;
};
export const createPettyCashFund = async (authAxios, data) => {
  const resp = await authAxios.post('petty-cash-funds/', data);
  return resp.data;
};
export const updatePettyCashFund = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`petty-cash-funds/${id}/`, data);
  return resp.data;
};
export const replenishPettyCashFund = async (authAxios, id, amount) => {
  const resp = await authAxios.post(`petty-cash-funds/${id}/replenish/`, { amount });
  return resp.data;
};
export const listPettyCashTransactions = async (authAxios, params={}) => {
  const resp = await authAxios.get('petty-cash-transactions/', { params });
  return resp.data?.results || resp.data;
};
export const createPettyCashTransaction = async (authAxios, data) => {
  const resp = await authAxios.post('petty-cash-transactions/', data);
  return resp.data;
};

// --- Firm Expenses ---
export const listFirmExpenses = async (authAxios, params={}) => {
  const resp = await authAxios.get('firm-expenses/', { params });
  return resp.data?.results || resp.data;
};
export const createFirmExpense = async (authAxios, data) => {
  const resp = await authAxios.post('firm-expenses/', data);
  return resp.data;
};
export const updateFirmExpense = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`firm-expenses/${id}/`, data);
  return resp.data;
};
export const approveFirmExpense = async (authAxios, id) => {
  const resp = await authAxios.post(`firm-expenses/${id}/approve/`);
  return resp.data;
};
export const payFirmExpense = async (authAxios, id, data) => {
  const resp = await authAxios.post(`firm-expenses/${id}/pay/`, data);
  return resp.data;
};

// --- Accounting Reports ---
export const fetchTrialBalance = async (authAxios, params={}) => {
  const resp = await authAxios.get('reports/trial-balance/', { params });
  return resp.data;
};
export const fetchIncomeStatement = async (authAxios, params={}) => {
  const resp = await authAxios.get('reports/income-statement/', { params });
  return resp.data;
};
export const fetchCreditorAging = async (authAxios) => {
  const resp = await authAxios.get('reports/creditor-aging/');
  return resp.data;
};

// =============================================================================
// Phase 2: Bank Accounts, Bank Reconciliation & VAT Returns
// =============================================================================

// --- Bank Accounts ---
export const listBankAccounts = async (authAxios, params={}) => {
  const resp = await authAxios.get('bank-accounts/', { params });
  return resp.data?.results || resp.data;
};
export const createBankAccount = async (authAxios, data) => {
  const resp = await authAxios.post('bank-accounts/', data);
  return resp.data;
};
export const updateBankAccount = async (authAxios, id, data) => {
  const resp = await authAxios.patch(`bank-accounts/${id}/`, data);
  return resp.data;
};
export const deleteBankAccount = async (authAxios, id) => {
  await authAxios.delete(`bank-accounts/${id}/`);
};

// --- Bank Statements ---
export const listBankStatements = async (authAxios, params={}) => {
  const resp = await authAxios.get('bank-statements/', { params });
  return resp.data?.results || resp.data;
};
export const createBankStatement = async (authAxios, data) => {
  const resp = await authAxios.post('bank-statements/', data);
  return resp.data;
};
export const fetchBankStatement = async (authAxios, id) => {
  const resp = await authAxios.get(`bank-statements/${id}/`);
  return resp.data;
};
export const importBankStatementCSV = async (authAxios, id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const resp = await authAxios.post(`bank-statements/${id}/import-csv/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return resp.data;
};

// --- Bank Statement Lines ---
export const listBankStatementLines = async (authAxios, params={}) => {
  const resp = await authAxios.get('bank-statement-lines/', { params });
  return resp.data?.results || resp.data;
};
export const createBankStatementLine = async (authAxios, data) => {
  const resp = await authAxios.post('bank-statement-lines/', data);
  return resp.data;
};
export const matchBankStatementLine = async (authAxios, lineId, journalEntryId) => {
  const resp = await authAxios.post(`bank-statement-lines/${lineId}/match/`, { journal_entry_id: journalEntryId });
  return resp.data;
};
export const unmatchBankStatementLine = async (authAxios, lineId) => {
  const resp = await authAxios.post(`bank-statement-lines/${lineId}/unmatch/`);
  return resp.data;
};

// --- Bank Reconciliations ---
export const listBankReconciliations = async (authAxios, params={}) => {
  const resp = await authAxios.get('bank-reconciliations/', { params });
  return resp.data?.results || resp.data;
};
export const createBankReconciliation = async (authAxios, data) => {
  const resp = await authAxios.post('bank-reconciliations/', data);
  return resp.data;
};
export const fetchBankReconciliation = async (authAxios, id) => {
  const resp = await authAxios.get(`bank-reconciliations/${id}/`);
  return resp.data;
};
export const completeBankReconciliation = async (authAxios, id) => {
  const resp = await authAxios.post(`bank-reconciliations/${id}/complete/`);
  return resp.data;
};

// --- VAT Returns ---
export const listVATReturns = async (authAxios, params={}) => {
  const resp = await authAxios.get('vat-returns/', { params });
  return resp.data?.results || resp.data;
};
export const createVATReturn = async (authAxios, data) => {
  const resp = await authAxios.post('vat-returns/', data);
  return resp.data;
};
export const fetchVATReturn = async (authAxios, id) => {
  const resp = await authAxios.get(`vat-returns/${id}/`);
  return resp.data;
};
export const calculateVATReturn = async (authAxios, id) => {
  const resp = await authAxios.post(`vat-returns/${id}/calculate/`);
  return resp.data;
};
export const fileVATReturn = async (authAxios, id, data={}) => {
  const resp = await authAxios.post(`vat-returns/${id}/file/`, data);
  return resp.data;
};
export const exportVATReturnPDF = async (authAxios, id) => {
  const resp = await authAxios.get(`vat-returns/${id}/export-pdf/`, { responseType: 'blob' });
  return resp.data;
};

export default api;